import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import { saveAuth, clearAuth } from "../../utils/authStorage";
import axios from "axios";

/* ============================
   🔐 REGISTER VENDOR
============================ */
export const registerVendor = createAsyncThunk(
  "auth/registerVendor",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/register-vendor", formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Registration failed"
      );
    }
  }
);

/* ============================
   🔑 LOGIN VENDOR (EMAIL)
============================ */
export const loginVendor = createAsyncThunk(
  "auth/loginVendor",
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/login-vendor", credentials);
      await saveAuth(res.data.token, res.data.user);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Login failed"
      );
    }
  }
);

/* ============================
   📲 SEND OTP (VENDOR)
============================ */
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async ({ phoneNumber }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/vendor/get-otp`,
        { phoneNumber }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to send OTP"
      );
    }
  }
);

/* ============================
   ✅ VERIFY OTP (VENDOR)
============================ */
export const verifyOtpLogin = createAsyncThunk(
  "auth/verifyOtpLogin",
  async ({ phoneNumber, otp }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/vendor/verify-otp`,
        { phoneNumber, otp }
      );
      await saveAuth(res.data.token, res.data.user);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "OTP verification failed"
      );
    }
  }
);

/* =====================================================
   👤 CUSTOMER SEND OTP
===================================================== */
export const sendCustomerOtp = createAsyncThunk(
  "auth/sendCustomerOtp",
  async ({ phoneNumber }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URI}/auth/customer/send-otp`,
        { phoneNumber }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to send OTP"
      );
    }
  }
);

/* =====================================================
   👤 CUSTOMER VERIFY OTP
===================================================== */
export const verifyCustomerOtp = createAsyncThunk(
  "auth/verifyCustomerOtp",
  async ({ phoneNumber, otp }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URI}/auth/customer/verify-otp`,
        { phoneNumber, otp }
      );
      await saveAuth(res.data.token, res.data.user);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "OTP verification failed"
      );
    }
  }
);

/* ============================
   🔐 REGISTER ADMIN
============================ */
export const registerAdmin = createAsyncThunk(
  "auth/registerAdmin",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/admin/register", formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Admin registration failed"
      );
    }
  }
);

/* ============================
   🔑 LOGIN ADMIN (EMAIL)
============================ */
export const loginAdmin = createAsyncThunk(
  "auth/loginAdmin",
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/admin/login", credentials);
      await saveAuth(res.data.token, res.data.user);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Admin login failed"
      );
    }
  }
);

/* ============================
   📲 SEND OTP ADMIN
============================ */
export const sendAdminOtp = createAsyncThunk(
  "auth/sendAdminOtp",
  async ({ phoneNumber }, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/admin/send-otp", { phoneNumber });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to send admin OTP"
      );
    }
  }
);

/* ============================
   ✅ VERIFY OTP ADMIN
============================ */
export const verifyAdminOtp = createAsyncThunk(
  "auth/verifyAdminOtp",
  async ({ phoneNumber, otp }, { rejectWithValue }) => {
    try {
      const res = await api.post("auth/admin/verify-otp", { phoneNumber, otp });
      await saveAuth(res.data.token, res.data.user);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Admin OTP verification failed"
      );
    }
  }
);

/* ============================
   AUTH SLICE
============================ */
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    loading: false,
    error: null,
    successMessage: null,
  },

  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },

    hydrateAuth: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
    },

    logout: (state) => {
      state.user = null;
      state.token = null;
      clearAuth();
    },

    clearAuthState: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder
      /* -------------------- VENDOR -------------------- */
      .addCase(registerVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(registerVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(loginVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendOtp.pending, (state) => { state.loading = true; })
      .addCase(sendOtp.fulfilled, (state, action) => { state.loading = false; state.successMessage = action.payload.message; })
      .addCase(sendOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(verifyOtpLogin.pending, (state) => { state.loading = true; })
      .addCase(verifyOtpLogin.fulfilled, (state, action) => { state.loading = false; state.token = action.payload.token; state.user = action.payload.user; state.successMessage = action.payload.message; })
      .addCase(verifyOtpLogin.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      /* -------------------- CUSTOMER -------------------- */
      .addCase(sendCustomerOtp.pending, (state) => { state.loading = true; })
      .addCase(sendCustomerOtp.fulfilled, (state, action) => { state.loading = false; state.successMessage = action.payload.message; })
      .addCase(sendCustomerOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(verifyCustomerOtp.pending, (state) => { state.loading = true; })
      .addCase(verifyCustomerOtp.fulfilled, (state, action) => { state.loading = false; state.token = action.payload.token; state.user = action.payload.user; state.successMessage = action.payload.message; })
      .addCase(verifyCustomerOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      /* -------------------- ADMIN -------------------- */
      .addCase(registerAdmin.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(registerAdmin.fulfilled, (state, action) => { state.loading = false; state.successMessage = action.payload.message; })
      .addCase(registerAdmin.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(loginAdmin.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginAdmin.fulfilled, (state, action) => { state.loading = false; state.token = action.payload.token; state.user = action.payload.user; })
      .addCase(loginAdmin.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(sendAdminOtp.pending, (state) => { state.loading = true; })
      .addCase(sendAdminOtp.fulfilled, (state, action) => { state.loading = false; state.successMessage = action.payload.message; })
      .addCase(sendAdminOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(verifyAdminOtp.pending, (state) => { state.loading = true; })
      .addCase(verifyAdminOtp.fulfilled, (state, action) => { state.loading = false; state.token = action.payload.token; state.user = action.payload.user; state.successMessage = action.payload.message; })
      .addCase(verifyAdminOtp.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { setUser, hydrateAuth, logout, clearAuthState } = authSlice.actions;
export default authSlice.reducer;
